/**
 * frontend/src/lib/api.js
 * ========================
 * Axios instance for Chekn API.
 *
 * INTERCEPTORS
 * ────────────
 * Request:
 *   - Attaches JWT from sessionStorage on every request (manager dashboard)
 *   - Staff check-in token goes in ?token= query param, not headers
 *
 * Response — handled error codes:
 *   401  Manager dashboard only → clear token, redirect to /login
 *        /checkin surface → show in-page error (token is URL-based)
 *   429  Rate limited → emit 'chekn:rate-limited' event with retryAfter
 *        The UI listens for this and shows a countdown toast
 *   403  Plan gate → emit 'chekn:plan-gate' event with structured detail
 *        { code, message, current_plan, required_plan }
 *        The UI listens and opens the upgrade modal
 *
 * AUTH STORAGE DECISION
 * ─────────────────────
 * Manager dashboard → sessionStorage (localStorage blocked in cross-origin iframes on Safari)
 * Staff check-in   → token in URL → localStorage (not iframed, persists across app opens)
 */

import axios from 'axios'

export const BASE_URL = import.meta.env.VITE_API_URL || ''

// ─── Instance ──────────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Custom events ────────────────────────────────────────────────────────
// Decouples interceptors from React component tree.
// Components subscribe via useEffect + window.addEventListener.

export const API_EVENTS = {
  RATE_LIMITED:  'chekn:rate-limited',   // detail: { retryAfter, path }
  PLAN_GATE:     'chekn:plan-gate',       // detail: { code, message, current_plan, required_plan }
  UNAUTHORIZED:  'chekn:unauthorized',    // detail: { path }
}

function emit(name, detail = {}) {
  window.dispatchEvent(new CustomEvent(name, { detail }))
}

// ─── Request interceptor — attach JWT ──────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('chekn_token')
    if (token) config.headers.Authorization = `Bearer ${token}`

    // When the frontend is on lacave.chekn.io but calls api.chekn.io,
    // the Host header won't contain the tenant subdomain. Send it explicitly.
    const parts = window.location.hostname.split('.')
    if (parts.length >= 3) {
      config.headers['X-Tenant-Subdomain'] = parts[0]
    }

    return config
  },
  (error) => Promise.reject(error)
)

// ─── Response interceptor ─────────────────────────────────────────────────

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status   = error.response?.status
    const detail   = error.response?.data?.detail
    const path     = error.config?.url || ''
    const headers  = error.response?.headers || {}

    // ── 429 Rate limited ─────────────────────────────────────────────────
    if (status === 429) {
      const retryAfter = parseInt(headers['retry-after'] || '60', 10)
      emit(API_EVENTS.RATE_LIMITED, { retryAfter, path })
      // Mutate the error so callers can detect it
      error.isRateLimited = true
      error.retryAfter = retryAfter
    }

    // ── 403 Plan gate ─────────────────────────────────────────────────────
    if (status === 403 && detail?.code?.startsWith('PLAN_')) {
      emit(API_EVENTS.PLAN_GATE, {
        code:         detail.code,
        message:      detail.message,
        current_plan: detail.current_plan,
        required_plan: detail.required_plan,
      })
      error.isPlanGate = true
    }

    // ── 401 Unauthorized — manager dashboard only ─────────────────────────
    if (status === 401) {
      const isCheckin = window.location.pathname.startsWith('/checkin')
      if (!isCheckin) {
        sessionStorage.removeItem('chekn_token')
        emit(API_EVENTS.UNAUTHORIZED, { path })
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// ─── Auth helpers ──────────────────────────────────────────────────────────

export const authApi = {
  login:          (email, password) => api.post('/api/auth/login', { email, password }),
  me:             ()                => api.get('/api/auth/me'),
  updateC7:       (data, { validate = true } = {}) => api.patch(`/api/auth/settings/c7?validate=${validate}`, data),
  updateC7Settings: (data, { validate = true } = {}) => api.patch(`/api/auth/settings/c7?validate=${validate}`, data),
  validateInvite: (token)           => api.get('/api/auth/invite/validate', { params: { token } }),
  registerInvite: (data)            => api.post('/api/auth/register-invite', data),
  setToken:       (t)               => sessionStorage.setItem('chekn_token', t),
  clearToken:     ()                => sessionStorage.removeItem('chekn_token'),
  getToken:       ()                => sessionStorage.getItem('chekn_token'),
  isAuthenticated: ()               => !!sessionStorage.getItem('chekn_token'),
}

// ─── Partners ──────────────────────────────────────────────────────────────

export const partnersApi = {
  list:       (params) => api.get('/api/partners', { params }),
  get:        (id)     => api.get(`/api/partners/${id}`),
  create:     (data)   => api.post('/api/partners', data),
  update:     (id, d)  => api.patch(`/api/partners/${id}`, d),
  deactivate: (id)     => api.delete(`/api/partners/${id}`),
  stats:      (id, p)  => api.get(`/api/partners/${id}/stats`, { params: p }),
}

// ─── Check-ins ─────────────────────────────────────────────────────────────

export const checkinsApi = {
  // Staff surface — token as query param
  submit:          (token, data) =>
    api.post(`/api/checkins/submit?token=${encodeURIComponent(token)}`, data),

  // Dashboard surface
  list:            (params) => api.get('/api/checkins', { params }),

  // Token management
  listTokens:      ()     => api.get('/api/checkins/staff-tokens'),
  createToken:     (data) => api.post('/api/checkins/staff-tokens', data),
  deactivateToken: (id)   => api.patch(`/api/checkins/staff-tokens/${id}`),
}

// ─── Sync ──────────────────────────────────────────────────────────────────

export const syncApi = {
  trigger: (data)  => api.post('/api/sync/trigger', data),
  logs:    (limit) => api.get('/api/sync/logs', { params: { limit } }),
  log:     (id)    => api.get(`/api/sync/logs/${id}`),
  status:  ()      => api.get('/api/sync/status'),
}

// ─── Dashboard ─────────────────────────────────────────────────────────────

export const dashboardApi = {
  stats:    (params) => api.get('/api/dashboard/stats', { params }),
  partners: (params) => api.get('/api/dashboard/partners', { params }),
}

// ─── Billing ───────────────────────────────────────────────────────────────

export const billingApi = {
  portal:   ()         => api.get('/api/billing/portal'),
  checkout: (planTier) =>
    api.post('/api/billing/checkout', null, { params: { plan_tier: planTier } }),
  status:   ()         => api.get('/api/billing/status'),
}

export default api
